import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useEffect, useState } from 'react';
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { usePlayer } from '../context/PlayerContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { getThemeColors } from '../utils/themeStyles';
import { getScenarioForMission } from '../data/scenarios';
import { TYPE_COLOR, glow, glowText } from '../utils/cyb';
import { ApiError } from '../api/client';
import { useControllersBlocked } from '../components/ControllersBlocked';
import MissionControllers from '../components/MissionControllers';
import RichText from '../components/RichText';
import DiagramViewer from '../components/DiagramViewer';
import ElementHeader from '../components/ElementHeader';
import HeaderNav from '../components/HeaderNav';
import { buildNavSequence, useItemNav } from '../utils/itemNav';
import { useElementVisited } from '../utils/elementProgress';
import { StatusPill, UsActivityPanel, US_COLOR, US_HANDS_OVER, US_ICON, US_LABEL } from '../components/UsDetail';
import { abandonUs, listUserStories, setUsStatus, UsDiagnostic, UsStatus } from '../api/userStories';
import { missingUsReason } from '../utils/missingUsReason';
import { useT } from '../i18n/useT';
import { sameElement } from '../utils/elementId';
import CybDialog from '../components/CybDialog';
import { useAuth } from '../context/AuthContext';
import { useMissionScenario } from '../hooks/useMissionScenario';

// Libellés joueur des statuts serveur (l'app ne montre jamais BACKLOG/ASSIGNED comme des
// choix : ils sont posés par le moteur).
// Libellés et couleurs de statut : table UNIQUE, importée de UsDetail.
/** Une PERSONNE de l'US (demandeur / assigné) : avatar, nom, rôle — la fiche s'ouvre au clic.
 *  Composant unique : demandeur et assigné doivent se présenter de la même façon. */
function PersonRow({ label, name, role, avatarUrl, onPress, colors }: {
  label: string; name?: string | null; role?: string | null;
  avatarUrl?: string | null; onPress?: () => void; colors: any;
}) {
  if (!name) return null;
  return (
    <TouchableOpacity activeOpacity={0.8} onPress={onPress}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 }}>
      {avatarUrl
        ? <Image source={{ uri: avatarUrl }} style={{ width: 36, height: 36, borderRadius: 18 }} resizeMode="cover" />
        : <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: colors.surfaceSecondary,
            alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ color: colors.textSecondary }}>{name.slice(0, 1)}</Text>
          </View>}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontSize: 11, color: colors.textTertiary }}>{label}</Text>
        <Text style={{ fontSize: 13, fontWeight: '600', color: colors.textPrimary }} numberOfLines={1}>{name}</Text>
        {!!role && <Text style={{ fontSize: 11, color: colors.textSecondary }} numberOfLines={1}>{role}</Text>}
      </View>
    </TouchableOpacity>
  );
}

//: Beyond this many characters the objective gets a "see more" — CCT-396.
//:
//: ⚠️ A LENGTH, NOT A MEASURED HEIGHT. Measuring the rendered block needs `onTextLayout`, whose
//: line count is unreliable on web; a threshold is deterministic, testable, and errs on the side
//: of offering the toggle to a text that would have fitted — harmless — rather than clamping one
//: that never shows a way out.
const SEUIL_REPLI = 320;


export default function TaskDetailScreen(
  { navigation: navProp, route: routeProp, onStatusChanged }:
  {
    navigation?: any; route?: any;
    /**
     * The host is told the status MOVED — CCT-477 / tableau de bord.
     *
     * ⚠️ WITHOUT IT, THE BOARD LIED. The dashboard shows this very screen in a modal, and it
     * reloaded only after a DRAG or an abandon — never after a status changed inside the modal.
     * The engine did fire (measured on dev: the owner answers in the thread within ten seconds,
     * for the bare id as for the `el-` form), but the card stayed in its old column and nothing
     * said otherwise. From the mission timeline the same gesture navigates to the conversation,
     * so the reaction is visible; from the board it was invisible, and read as "the engine does
     * not start".
     */
    onStatusChanged?: (statut: UsStatus) => void;
  } = {}) {
  const t = useT();
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  const { itemId, title: routeTitle } = route.params ?? {};
  const missionId = Number(route.params?.missionId) || undefined;
  const chapterId = Number(route.params?.chapterId) || undefined;
  const scenarioLoad = useMissionScenario(missionId);
  const { completeItem } = useStoryProgress();
  const scenario = missionId ? getScenarioForMission(missionId) : undefined;
  const scenarioChapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
  // Recherche par id d'item (blob string), repli sur l'index pour le legacy numérique.
  const sequenceItem = itemId != null
    ? (scenarioChapter?.sequence?.find(s => String(s.id) === String(itemId))
        ?? scenarioChapter?.sequence?.[Number(itemId) - 1])
    : undefined;
  const scenarioTask =
    sequenceItem?.type === 'task' &&
    typeof sequenceItem.content === 'object' &&
    'steps' in sequenceItem.content &&
    'objective' in sequenceItem.content
      ? sequenceItem
      : undefined;

  // CodeController (CCT-73) : le serveur ne sert `controller: 'code'` QUE s'il y a une
  // template exploitable — un lien « Ouvrir le projet » sans template mènerait à une erreur.
  const taskContent = (scenarioTask?.content ?? {}) as any;
  const codeTemplateId: number | undefined =
    taskContent.controller === 'code' && taskContent.projectTemplateId
      ? Number(taskContent.projectTemplateId) : undefined;

  // Le type est ANCRÉ sur la forme réellement construite : sans ancrage, TypeScript infère
  // `never` pour la branche vide et tous les accès `task.*` deviennent des erreurs.
  type TaskVue = {
    itemId: any; title: string; subtitle: string; points: number;
    priority: 'High'; difficulty: string; estimatedTime: string;
    objective: string; context: string;
    steps: { title: string; detail: string }[];
    criteria: { id: number; text: string }[];
    resources: { type?: string; title?: string; url?: string }[];
    /** CCT-477 — a draw.io diagram attached to the task, and the author's zoom. */
    diagramUrl?: string;
    diagramZoom?: number;
  };
  const validation = (scenarioTask?.content as any)?.validation;
  // Keep authored lists as separate checkboxes; older missions still use text.
  const criterionValues = Array.isArray(validation)
    ? (validation.length ? validation : [t('task.defaultValidation')])
    : [validation ?? t('task.defaultValidation')];
  const task: TaskVue | undefined = scenarioTask
    ? {
        itemId: itemId ?? 0,
        title: scenarioTask.title,
        subtitle: scenario?.certification ? `${scenario.certification} — Task` : 'Task',
        points: 20,
        priority: 'High' as const,
        difficulty: 'Moyen' as const,
        estimatedTime: `${scenarioTask.duration ?? 20} min`,
        objective: String((scenarioTask.content as any).objective ?? ''),
        // ⚠️ THE AUTHORED CONTEXT, NOT THE MISSION TITLE. This line read `scenario.title`: the
        // "Contexte" banner repeated the name of the mission on every task, and what the author
        // had actually written was displayed nowhere. Invisible until the header started naming
        // the mission one line above. `platform` is the legacy key for the same value.
        context: String((scenarioTask.content as any).context
                        ?? (scenarioTask.content as any).platform ?? ''),
        steps: ((scenarioTask.content as any).steps ?? []).map((s: any, idx: number) => ({
          number: Number(s.stepNumber ?? idx + 1),
          title: String(s.action ?? t('task.step', { n: idx + 1 })),
          detail: String(s.detail ?? ''),
        })),
        criteria: criterionValues.map((value, index) => ({
          id: index + 1,
          text: String(value?.text ?? value),
        })),
        resources: [],
        // CCT-477 — served inside `content`, like `projectTemplateId`.
        diagramUrl: (scenarioTask.content as any).diagramUrl
          ? String((scenarioTask.content as any).diagramUrl) : undefined,
        diagramZoom: (scenarioTask.content as any).diagramZoom
          ? Number((scenarioTask.content as any).diagramZoom) : undefined,
      }
    : undefined;   // no more fallback to bundled tasks (CCT-253)

  const itemNav = useItemNav(route, navigation, itemId, buildNavSequence(scenario?.chapters ?? []));
  // Progression PILOTEE PAR LE MODELE (cf. utils/elementProgress) : c'est l'element
  // AFFICHE qui est enregistre, quel que soit le chemin emprunte pour l'ouvrir.
  useElementVisited(route?.params?.missionId, route?.params?.itemId);

  // STATUT SERVEUR de l'US (CCT-74). C'est le moteur qui réagit : IN_PROGRESS déclenche les
  // relances, BLOCKED un hint du personnage owner, TO_REVIEW l'inspection de la livraison.
  // VALIDATED / REFUSED ne sont PAS proposés : seule l'inspection les pose (le serveur les
  // refuse de toute façon — sans quoi le joueur s'auto-validerait).
  const { getAccessToken } = useAuth();
  const [usStatus, setUsStatusState] = useState<UsStatus | null>(null);
  // Abandon : « Abandonnée » ≠ « Refusée ». On garde l'info et l'effet pour la confirmation.
  const [usAbandoned, setUsAbandoned] = useState(false);
  const [usAbandonEffect, setUsAbandonEffect] = useState<'unlock_next' | 'abandon_mission'>('unlock_next');
  // Divulgation de la solution (CCT-100) : autorisée par l'auteur ET la tâche terminée.
  const [usHasSolution, setUsHasSolution] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [settable, setSettable] = useState<UsStatus[]>([]);
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const [objectifDeplie, setObjectifDeplie] = useState(false);
  //: Comments/journal zone folded BY DEFAULT on a phone: it grows with every turn of play, and
  //: one opens a task to act, not to re-read its history.
  const [activiteDepliee, setActiviteDepliee] = useState(false);
  const { modal: modaleControleurs, bloquer: bloquerControleurs } = useControllersBlocked();
  // `true` seulement si le serveur a RÉPONDU sans connaître cette US — pas si l'appel a
  // échoué. La nuance porte tout le message affiché.
  const [usAbsente, setUsAbsente] = useState(false);
  // Faits servis par le SERVEUR pour expliquer l'absence : lui seul voit la storyline,
  // l'instance du moteur et la licence.
  const [usDiag, setUsDiag] = useState<UsDiagnostic | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  // CCT-155 — INTERVENANTS : le personnage OWNER de l'US est le demandeur (c'est lui qui signe
  // relances et validations côté moteur) ; l'assigné, c'est le joueur, dans le rôle qu'il tient.
  const [ownerCharId, setOwnerCharId] = useState<number | null>(null);
  const [charCard, setCharCard] = useState<any | null>(null);
  const wide = useWindowDimensions().width >= 900;
  const { playerName, playerAvatar } = usePlayer();
  const missionChars: any[] = (getScenarioForMission(Number(missionId)) as any)?.characters ?? [];
  const requester = missionChars.find((c) => c.characterId === ownerCharId) ?? null;
  const assigneeChar = missionChars.find((c) => c.isPlayer) ?? null;
  const assigneeName = playerName || 'Vous';
  const assigneeRole = assigneeChar?.role ?? '';
  const assigneeAvatar = assigneeChar?.avatarUrl ?? null;

  useEffect(() => {
    void (async () => {
      if (missionId == null || itemId == null) return;
      try {
        const r = await listUserStories(getAccessToken(), missionId);
        const mine = (r.userStories ?? []).find(
          (u) => sameElement(u.element_id, itemId));
        if (mine) {
          setUsStatusState(mine.status);
          setUsAbandoned(!!mine.abandoned);
          setUsAbandonEffect(mine.abandon_effect ?? 'unlock_next');
          setUsHasSolution(!!mine.has_solution);
          setOwnerCharId(mine.owner_character_id ?? null);
        }
        setSettable(mine?.settable ?? r.settable ?? []);
        // ON DIT CE QU'ON SAIT (CCT-253). L'appel a RÉUSSI mais n'a rendu aucune US pour cet
        // élément : la tâche affichée vient du scénario BUNDLÉ, le moteur n'en a pas
        // d'équivalent. Le bloc Statut était alors simplement absent — l'utilisateur voyait
        // une tâche sans statut sans qu'aucun élément d'écran ne l'explique. Un repli
        // silencieux de plus.
        setUsAbsente(!mine);
        setUsDiag(r.diagnostic ?? null);
      } catch {
        // L'appel lui-même a échoué (réseau, droits) : on ne peut RIEN affirmer sur l'US,
        // donc on n'affiche pas non plus de message qui serait peut-être faux.
        setUsAbsente(false);
        setUsDiag(null);
      }
    })();
  }, [missionId, itemId]);

  /** Le contexte de la tâche — un seul rendu, deux emplacements possibles. */
  const bandeauContexte = task?.context ? (
    <View style={[styles.contextCard, { backgroundColor: isDarkTheme ? '#1E3A5F' : '#EFF6FF', borderColor: isDarkTheme ? '#2563EB44' : '#BFDBFE' }]}>
      <Ionicons name="information-circle" size={16} color="#3B82F6" />
      <RichText style={[styles.contextText, { color: isDarkTheme ? '#93C5FD' : '#1D4ED8' }]} value={task.context} />
    </View>
  ) : null;

  const changeStatus = async (st: UsStatus) => {
    if (missionId == null || itemId == null) return;
    setStatusBusy(true); setStatusErr(null);
    try {
      const r = await setUsStatus(getAccessToken(), missionId, String(itemId), st);
      setUsStatusState(r.status);
      // The host reloads on the SERVER's answer, not on what was asked: the engine may already
      // have moved the task elsewhere.
      onStatusChanged?.(r.status);
      // CCT-345 — HANDING THE TASK OVER OPENS THE CONVERSATION, it does not spin a pill. The
      // server has already written the player's own sentence in the thread ("I have moved it to
      // review, can you look?"); the character's answer lands in the same thread when the engine
      // has one. Before this, the request waited for the whole inspection and the only thing the
      // player saw was "Synchronisation…", which names nothing.
      if (US_HANDS_OVER.includes(st)) {
        navigation.navigate('UsChat' as never,
          { missionId, elementId: String(itemId) } as never);
      }
    } catch (e) {
      // ⚠️ A REFUSAL IS NOT A FAILURE, AND MY FIRST MESSAGE CONFUSED THE TWO. The hand-over is
      // refused (403) while the task's controller is not connected — measured on dev, two 403 in
      // a row — and the screen answered "reload, then try again": an instruction that cannot
      // work, for a condition retrying never changes. The refusal now names the tool and where
      // to activate it; only a real failure keeps the generic sentence.
      const detail = (e instanceof ApiError && e.status === 403
        ? (e.body as { detail?: { error?: string; controllers?: string[] } })?.detail
        : undefined);
      // ⚠️ A REFUSAL OF CONTROLLERS RAISES THE BLOCKING DIALOG, not a line of red text. It is the
      // SAME refusal as the one that bars the mission at launch, so it says the same words and
      // leads to the same place — the mission page, where activating is possible. A message left
      // at the bottom of a panel names a gesture without leading to it.
      if (detail?.error === 'require_controllers') {
        bloquerControleurs(missionId ?? null, detail.controllers ?? []);
      } else {
        setStatusErr(t('task.statusFailed'));
      }
    } finally { setStatusBusy(false); }
  };
  const [checkedCriteria, setCheckedCriteria] = useState<Set<number>>(new Set());
  const [isDone, setIsDone] = useState(false);

  const toggleCriteria = (id: number) => {
    setCheckedCriteria(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const allChecked = task ? checkedCriteria.size >= task.criteria.length : false;

  const getDifficultyColor = (d: string) => {
    if (d === 'Facile') return '#10B981';
    if (d === 'Moyen') return '#F59E0B';
    return '#EF4444';
  };

  const displayTitle = task?.title ?? routeTitle ?? 'Tache';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <ElementHeader
            missionTitle={(route.params as any)?.missionTitle ?? ''}
            chapterTitle={(route.params as any)?.chapterTitle ?? ''}
            elementTitle={displayTitle}
          />
        </View>
        <View style={[styles.pointsBadge, { backgroundColor: '#EF4444' + '22' }]}>
          <Text style={[styles.pointsText, { color: '#EF4444' }]}>+{task?.points ?? 0} XP</Text>
        </View>
        {/* La validation devient la flèche « terminer » : active seulement quand les critères
            sont cochés (sinon désactivée, pour que la règle reste lisible). */}
        {/* ← élément précédent · → valide la tâche PUIS enchaîne (inactive tant que les
            critères ne sont pas cochés : la règle reste lisible sans texte). */}
        <HeaderNav
          onPrev={itemNav.goPrev}
          // CCT-164 — la flèche « suivant » n'est plus CONDITIONNÉE aux critères : une tâche
          // déjà terminée (validée ou abandonnée) n'a plus rien à cocher, et le joueur se
          // retrouvait bloqué sans moyen de poursuivre l'histoire. Cocher reste ce qui la
          // VALIDE ; naviguer reste toujours possible.
          onNext={task ? () => {
            if (allChecked) {
              setIsDone(true);
              if (itemId != null && missionId != null) void completeItem(missionId, String(itemId));
            }
            itemNav.goNext?.();
          } : undefined}
          accent={TYPE_COLOR.task}
          isLast={itemNav.isLast}
        />
      </View>

      <ScrollView style={[styles.scroll, { backgroundColor: colors.background }]}
        contentContainerStyle={{ backgroundColor: colors.background }}
        showsVerticalScrollIndicator={false} overScrollMode="never" bounces={false}>
        <View style={styles.content}>
          {!task && (
            <View style={styles.card}>
              {scenarioLoad.status === 'loading'
                ? <Text style={{ color: colors.textPrimary }}>{t('chapter.loading')}</Text>
                : <TouchableOpacity onPress={scenarioLoad.retry} accessibilityRole="button">
                    <Text style={{ color: colors.textPrimary }}>{t('common.retry')}</Text>
                  </TouchableOpacity>}
            </View>
          )}

          {/* Badges de difficulté/durée retirés (CCT-161) : ils faisaient DOUBLON avec le
              bloc « Effort », qui les porte désormais avec les XP. */}
          {/* ⚠️ LE CONTEXTE A QUITTÉ LE HAUT DE L'ÉCRAN. C'est le texte le plus long de la page
              après l'objectif, et il occupait le premier écran entier d'un téléphone : le statut,
              seule partie actionnable, n'apparaissait qu'après l'avoir fait défiler. Il vit
              maintenant dans l'activité de l'US, avec le reste de ce qui se LIT. Rendu ici
              seulement quand cette zone n'existe pas, pour qu'il ne disparaisse jamais. */}
          {task?.context && !usStatus ? bandeauContexte : null}

          {/* CCT-155 — deux colonnes : à GAUCHE ce qu'on lit et produit (objectif, critères,
              commentaires, activité), à DROITE l'état et les accès (statut, intervenants,
              projet, solution). En une seule colonne, le statut et l'accès à l'IDE se
              retrouvaient noyés sous les commentaires. Une seule colonne sous 900 px. */}
          <View accessibilityLabel={`us-cols-${wide}`}
            // ⚠️ `column-reverse` ON A PHONE, AND IT IS THE WHOLE POINT (CCT-396). This container
            // holds exactly TWO children: what one reads on the left, what one DOES on the right.
            // Stacked in source order, the status — the only actionable part — landed after the
            // objective, the criteria, the comments and the journal. Reversing the two blocks
            // costs nothing and puts the buttons where a thumb starts.
            style={{ flexDirection: wide ? 'row' : 'column-reverse', gap: 12,
                     alignItems: 'flex-start', width: '100%' }}>
            <View style={{ flex: wide ? 2 : undefined, width: wide ? undefined : '100%', minWidth: 0 }}>
          {/* Objective */}
          <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
            <View style={styles.cardHeader}>
              {/* A target, not a flag: the flag also headed the STATUS card, so the same symbol
                  stood for two different things on one screen. */}
              <Ionicons name="locate" size={18} color="#EF4444" />
              <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.objective')}</Text>
            </View>
            {/* ⚠️ THE OBJECTIVE IS THE LONGEST TEXT OF THE SCREEN, and on a phone it pushed
                everything else below the fold. Clamped, with a way out — never truncated for good. */}
            <RichText style={[styles.cardBody, { color: colors.textSecondary }]}
              value={task?.objective ?? displayTitle}
              numberOfLines={objectifDeplie ? undefined : 6} />
            {(task?.objective ?? '').length > SEUIL_REPLI ? (
              <TouchableOpacity onPress={() => setObjectifDeplie(v => !v)} style={{ marginTop: 6 }}>
                <Text style={{ color: colors.primary, fontSize: 13, fontWeight: '600' }}>
                  {t(objectifDeplie ? 'task.seeLess' : 'task.seeMore')}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>

          {/* CCT-477 — the diagram sits right UNDER the objective, because that is what it
              illustrates. Placed after the criteria it read as proof rather than as a brief. */}
          {task?.diagramUrl ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <Ionicons name="git-network" size={18} color={colors.primary} />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('diagram.title')}</Text>
              </View>
              <DiagramViewer url={task.diagramUrl} hauteur={300} zoom={task.diagramZoom} />
            </View>
          ) : null}

          {/* Steps */}
          {task?.steps && task.steps.length > 0 ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <Ionicons name="list" size={18} color="#3B82F6" />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.steps')}</Text>
              </View>
              {task.steps.map((step: any) => (
                <View key={step.number} style={styles.stepRow}>
                  <View style={[styles.stepNum, { backgroundColor: '#3B82F6' }]}>
                    <Text style={styles.stepNumText}>{step.number}</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <Text style={[styles.stepTitle, { color: colors.textPrimary }]}>{step.title}</Text>
                    <RichText style={[styles.stepDetail, { color: colors.textSecondary }]} value={step.detail} />
                  </View>
                </View>
              ))}
            </View>
          ) : null}

          {/* Acceptance criteria */}
          {task?.criteria && task.criteria.length > 0 ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <Ionicons name="checkmark-done" size={18} color="#10B981" />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>
                  Criteres d'acceptation ({checkedCriteria.size}/{task.criteria.length})
                </Text>
              </View>
              {task.criteria.map(c => {
                const checked = checkedCriteria.has(c.id);
                return (
                  <TouchableOpacity key={c.id} style={styles.criteriaRow} onPress={() => toggleCriteria(c.id)}>
                    <View style={[styles.checkBox, {
                      borderColor: checked ? '#10B981' : colors.border,
                      backgroundColor: checked ? '#10B981' : 'transparent',
                    }]}>
                      {checked && <Ionicons name="checkmark" size={12} color="#FFF" />}
                    </View>
                    <RichText style={[styles.criteriaText, { color: checked ? colors.textSecondary : colors.textPrimary }, checked && styles.strikethrough]}
                      value={c.text} />
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : null}

          {/* Comments + journal: the SAME component as the kanban's panel (CCT-99).
              ⚠️ FOLDED BY DEFAULT ON A PHONE, open on a wide screen: this zone grows with every
              turn of play, and on a narrow screen it pushed everything else out of sight. The
              title carries the gesture, as on the board's columns. */}
          {usStatus ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
              <TouchableOpacity activeOpacity={0.7} style={styles.cardHeader}
                onPress={() => setActiviteDepliee(v => !v)}>
                <Ionicons name="chatbubbles-outline" size={18} color={colors.textSecondary} />
                <Text style={[styles.cardTitle, { color: colors.textPrimary, flex: 1 }]}>
                  {t('task.activityZone')}
                </Text>
                <Ionicons name={(wide || activiteDepliee) ? 'chevron-up' : 'chevron-down'}
                  size={18} color={colors.textSecondary} />
              </TouchableOpacity>
              {(wide || activiteDepliee) && bandeauContexte}
              {missionId != null && (wide || activiteDepliee) && (
                <UsActivityPanel missionId={missionId} elementId={String(itemId)} statusKey={usStatus} />
              )}
            </View>
          ) : null}

          {/* Resources */}
          {task?.resources && task.resources.length > 0 ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <Ionicons name="book" size={18} color="#8B5CF6" />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.resources')}</Text>
              </View>
              {task.resources.map((r, i) => (
                <View key={i} style={[styles.resourceRow, { borderColor: colors.border }]}>
                  <Ionicons
                    name={r.type === 'video' ? 'play-circle-outline' : r.type === 'lab' ? 'flask-outline' : 'document-text-outline'}
                    size={18}
                    color="#8B5CF6"
                  />
                  <Text style={[styles.resourceTitle, { color: colors.primary }]}>{r.title}</Text>
                  <View style={[styles.resourceTypeBadge, { backgroundColor: '#8B5CF6' + '22' }]}>
                    <Text style={[styles.resourceTypeText, { color: '#8B5CF6' }]}>{r.type}</Text>
                  </View>
                </View>
              ))}
            </View>
          ) : null}
            </View>

            <View style={{ flex: wide ? 1 : undefined, width: wide ? undefined : '100%', minWidth: 0 }}>
          {/* Statut de l'US : c'est LUI qui pilote le moteur (relances, hint, inspection). */}
          {!usStatus && usAbsente ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                {/* NO status to show: a question mark, and not the icon of a state we would be
                    inventing. The card's whole point is that the server knows nothing here. */}
                <Ionicons name="help-circle-outline" size={18} color={colors.textSecondary} />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.status')}</Text>
              </View>
              <Text style={{ color: colors.textSecondary, fontSize: 13, marginTop: 6, lineHeight: 19 }}>
                {t('task.unknownToServer')}
              </Text>
              {/* ⚠️ WE NO LONGER ASSERT A CAUSE WE CANNOT VERIFY. The previous version concluded
                  « la storyline n'a pas été publiée dans cet environnement ». Measured on
                  EgoCrisis: it HAD been (4 authored user stories), the learner was assigned and
                  licensed — but the engine instance had been stopped for three weeks. The message
                  sent us looking in the wrong place. The server reports the facts and we read
                  them; when it reports none, we say we do not know. */}
              <Text style={{ color: colors.textTertiary, fontSize: 12, marginTop: 8, lineHeight: 18 }}>
                {missingUsReason(usDiag, t)}
              </Text>
            </View>
          ) : null}
          {usStatus ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                {/* The icon of the CURRENT status, in its colour: the card's header says where the
                    task stands before the eye reaches the pill. */}
                <Ionicons name={US_ICON[usStatus]} size={18}
                  color={usAbandoned ? colors.textSecondary : (US_COLOR[usStatus] ?? colors.textSecondary)} />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.status')}</Text>
                {/* Pastille PARTAGÉE (UsDetail) : le statut d'une US ne doit pas avoir deux
                    représentations selon l'écran qui l'affiche. */}
                <View style={{ marginLeft: 8 }}><StatusPill status={usStatus} abandoned={usAbandoned} /></View>
              </View>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {/* CCT-156 — une tâche ABANDONNÉE est un état DÉFINITIF : proposer d'en sortir
                    laissait croire à un retour en arrière que le serveur refuse. Les statuts
                    restent visibles (on veut lire l'historique), mais inertes.
                    Style aligné sur les badges « Moyen / 20 min » : même famille visuelle. */}
                {settable.map((st) => {
                  const active = st === usStatus;
                  const fige = usAbandoned || usStatus === 'READY' || statusBusy || active;
                  const c = US_COLOR[st] ?? TYPE_COLOR.task;
                  return (
                    /**
                     * ⚠️ L'ÉTAT ACTUEL ET LES ÉTATS À POSER SE RESSEMBLAIENT TROP. L'actif ne se
                     * distinguait que par une bordure de 1 px et deux crans d'opacité
                     * (`c+'24'` contre `c+'12'`) : sur un téléphone, impossible de dire lequel
                     * est le statut courant et lesquels sont des boutons. Désormais l'actif est
                     * PLEIN et coché ; les autres sont de simples contours — deux formes, pas
                     * deux nuances.
                     */
                    <TouchableOpacity key={st} disabled={fige} onPress={() => changeStatus(st)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active, disabled: fige }}
                      accessibilityLabel={`${US_LABEL[st] ? t(US_LABEL[st]) : st}${active ? ' — ' + t('task.status') : ''}`}
                      style={{ paddingHorizontal: 11, paddingVertical: 7, borderRadius: 8,
                        flexDirection: 'row', alignItems: 'center', gap: 6,
                        // Inerte parce que la tâche est close : estompé. Inerte parce que c'est
                        // DÉJÀ l'état courant : surtout pas, c'est l'information principale.
                        opacity: fige && !active ? 0.4 : 1,
                        borderWidth: active ? 0 : 1, borderColor: c + '66',
                        backgroundColor: active ? c : 'transparent' }}>
                      <Ionicons name={active ? 'checkmark-circle' : (US_ICON[st] ?? 'ellipse-outline')}
                        size={14} color={active ? '#FFFFFF' : c} />
                      <Text style={{ fontSize: 12.5, fontWeight: active ? '800' : '600',
                                     color: active ? '#FFFFFF' : c }}>
                        {US_LABEL[st] ? t(US_LABEL[st]) : st}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {statusErr ? (
                <Text style={{ color: colors.error, fontSize: 12, marginTop: 8, lineHeight: 17 }}>
                  {statusErr}
                </Text>
              ) : null}
              {usStatus === 'BLOCKED' || usStatus === 'REFUSED' ? (
                <TouchableOpacity
                  onPress={() => navigation.navigate('UsChat' as never, { missionId, elementId: String(itemId) } as never)}
                  style={{ marginTop: 10 }}>
                  <Text style={{ color: TYPE_COLOR.task, fontSize: 13, fontWeight: '700' }}>
                    {t('task.openDiscussion')} <Ionicons name="arrow-forward" size={13} />
                  </Text>
                </TouchableOpacity>
              ) : null}
              {/* Giving up: possible while the task is not finished (validated or already given
                  up on). A final status → a confirmation that states the impact. */}
              {usStatus !== 'VALIDATED' && usStatus !== 'READY' && !usAbandoned ? (
                <TouchableOpacity onPress={() => setAbandonOpen(true)} style={{ marginTop: 10 }}>
                  <Text style={{ color: '#ff2d55', fontSize: 13, fontWeight: '700' }}>
                    <Ionicons name="close-circle-outline" size={13} /> {t('task.abandon')}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}

          <CybDialog
            visible={abandonOpen}
            title={t('task.abandonConfirmTitle')}
            warning={usAbandonEffect === 'abandon_mission'}
            message={t(usAbandonEffect === 'abandon_mission'
              ? 'task.abandonConfirmDecisive'
              : 'task.abandonConfirmNormal')}
            confirmLabel={t('task.abandonConfirmLabel')}
            destructive
            busy={abandoning}
            onCancel={() => setAbandonOpen(false)}
            onConfirm={async () => {
              if (missionId == null || itemId == null) return;
              setAbandoning(true);
              try {
                await abandonUs(getAccessToken(), missionId, String(itemId));
                setUsStatusState('REFUSED'); setUsAbandoned(true); setAbandonOpen(false);
                onStatusChanged?.('REFUSED');
              } catch { /* le serveur reste la source de vérité */ }
              finally { setAbandoning(false); }
            }}
          />
          {/* ⚠️ THE ACTIVATIONS ARE SHOWN HERE, where the refusal happens (CCT-396). They lived
              only on the mission card: a player whose hand-over was refused for a missing access
              had to guess they should go back one screen. The component hides ITSELF when the
              mission requires no controller — no empty card appears on tasks that need none. */}
          {missionId != null ? <MissionControllers missionId={Number(missionId)} /> : null}

              {/* Intervenants (CCT-155) : QUI demande, QUI réalise — avec avatar et nom, et la
                  fiche du personnage au clic. L'US ne disait pas de qui elle venait. */}
              <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
                <View style={styles.cardHeader}>
                  <Ionicons name="people" size={18} color={colors.textSecondary} />
                  <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.people')}</Text>
                </View>
                <PersonRow label={t('task.requester')} name={requester?.name} role={requester?.role}
                  avatarUrl={requester?.avatarUrl} onPress={() => requester && setCharCard(requester)} colors={colors} />
                <PersonRow label={t('task.assignee')} name={assigneeName} role={assigneeRole}
                  avatarUrl={assigneeAvatar} onPress={() => assigneeChar && setCharCard(assigneeChar)} colors={colors} />
              </View>

              {/* CCT-156 — Difficulté et estimation : elles n'existaient que dans les badges de
                  l'en-tête, hors du champ de vision une fois la page défilée. */}
              <View style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}>
                <View style={styles.cardHeader}>
                  <Ionicons name="speedometer" size={18} color={colors.textSecondary} />
                  <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.effort')}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {!!task?.difficulty && (
                    <View style={[styles.metaBadge, { backgroundColor: getDifficultyColor(task.difficulty) + '22' }]}>
                      {/* The VALUE drives the colour, the LABEL is translated — never the reverse. */}
                      <Text style={[styles.metaBadgeText, { color: getDifficultyColor(task.difficulty) }]}>
                        {t(`task.difficulty.${task.difficulty}` as any)}
                      </Text>
                    </View>
                  )}
                  {!!task?.estimatedTime && (
                    <View style={[styles.metaBadge, { backgroundColor: colors.surfaceSecondary }]}>
                      <Ionicons name="time-outline" size={12} color={colors.textSecondary} />
                      <Text style={[styles.metaBadgeText, { color: colors.textSecondary }]}>{task.estimatedTime}</Text>
                    </View>
                  )}
                  {!!task?.points && (
                    <View style={[styles.metaBadge, { backgroundColor: colors.surfaceSecondary }]}>
                      <Text style={[styles.metaBadgeText, { color: colors.textSecondary }]}>+{task.points} XP</Text>
                    </View>
                  )}
                </View>
              </View>
          {/* CodeController : le point d'entrée du candidat vers l'IDE. Le projet est
              instancié à l'ouverture (idempotent : on retrouve son code en revenant). */}
          {codeTemplateId ? (
            <TouchableOpacity
              onPress={() => navigation.navigate('CodeIde' as never, {
                projectTemplateId: codeTemplateId, missionId, itemId, title: displayTitle,
              } as never)}
              style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }]}
            >
              <View style={styles.cardHeader}>
                <Ionicons name="code-slash" size={18} color={colors.textSecondary} />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.codeProject')}</Text>
              </View>
              <Text style={[styles.cardBody, { color: colors.textSecondary }]}>
                {t('task.codeProjectBody')}
              </Text>
              <Text style={{ color: TYPE_COLOR.task, fontSize: 13, fontWeight: '700', marginTop: 8 }}>
                {t('task.openProject')} <Ionicons name="arrow-forward" size={13} />
              </Text>
            </TouchableOpacity>
          ) : null}

          {/* Solution (CCT-100) : une fois la tâche TERMINÉE (validée ou abandonnée) et si
              l'auteur l'a autorisée, la template Solution s'ouvre en LECTURE SEULE mais
              EXÉCUTABLE. La garde réelle est SERVEUR (/projects/solution 403 sinon) ; on
              n'affiche la carte que si `has_solution` pour ne pas proposer un bouton mort. */}
          {usHasSolution && (usStatus === 'VALIDATED' || usAbandoned) ? (
            <TouchableOpacity
              onPress={() => navigation.navigate('CodeIde' as never, {
                solutionOf: String(itemId), missionId, readOnly: true,
                title: `Solution — ${displayTitle}`,
              } as never)}
              style={[styles.card, { backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: '#10B981' }]}
            >
              <View style={styles.cardHeader}>
                <Ionicons name="bulb" size={18} color="#10B981" />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{t('task.referenceSolution')}</Text>
              </View>
              <Text style={[styles.cardBody, { color: colors.textSecondary }]}>
                {t('task.solutionBody')}
              </Text>
              <Text style={{ color: '#10B981', fontSize: 13, fontWeight: '700', marginTop: 8 }}>
                {t('task.seeSolution')} <Ionicons name="arrow-forward" size={13} />
              </Text>
            </TouchableOpacity>
          ) : null}

            </View>
          </View>
        </View>
      </ScrollView>

      {/* FICHE du personnage — même geste partout : un avatar se clique et raconte qui il est. */}
      {!!charCard && (
        <TouchableOpacity activeOpacity={1} onPress={() => setCharCard(null)}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <View style={{ width: '100%', maxWidth: 420, borderRadius: 16, padding: 20, gap: 10,
            backgroundColor: colors.cardBackground, borderWidth: 1, borderColor: colors.border }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              {!!charCard.avatarUrl && (
                <Image source={{ uri: charCard.avatarUrl }} style={{ width: 56, height: 56, borderRadius: 28 }} resizeMode="cover" />
              )}
              <View style={{ flex: 1 }}>
                <Text style={{ color: colors.textPrimary, fontSize: 16, fontWeight: '700' }}>{charCard.name}</Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>{charCard.role}</Text>
                {!!charCard.isPlayer && (
                  <Text style={{ color: TYPE_COLOR.task, fontSize: 12, marginTop: 2 }}>Rôle tenu par {assigneeName}</Text>
                )}
              </View>
            </View>
            {!!charCard.description && (
              <Text style={{ color: colors.textSecondary, fontSize: 13, lineHeight: 19 }}>{charCard.description}</Text>
            )}
            <TouchableOpacity onPress={() => setCharCard(null)} style={{ alignSelf: 'flex-end', padding: 8 }}>
              <Text style={{ color: TYPE_COLOR.task, fontWeight: '600' }}>{t('task.close')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      )}

      {/* ⚠️ MOUNTED HERE, OR IT NEVER SHOWS. Setting the blocking state without mounting the
          dialog would reproduce the original defect exactly: a refusal nothing says. */}
      {modaleControleurs}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, gap: 10 },
  backBtn: { padding: 6 },
  headerInfo: { flex: 1 },
  headerTitle: { fontSize: 16, fontWeight: '700' },
  headerSub: { fontSize: 12, marginTop: 2 },
  pointsBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10 },
  pointsText: { fontSize: 13, fontWeight: '700' },
  scroll: { flex: 1 },
  content: { padding: 16 },
  metaRow: { flexDirection: 'row', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  metaBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10 },
  metaBadgeText: { fontSize: 12, fontWeight: '600' },
  contextCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, borderWidth: 1, borderRadius: 10, padding: 12, marginBottom: 14 },
  contextText: { flex: 1, fontSize: 13, lineHeight: 19, fontStyle: 'italic' },
  card: { borderRadius: 12, padding: 16, marginBottom: 14, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 4, elevation: 2 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  cardTitle: { fontSize: 15, fontWeight: '700' },
  cardBody: { fontSize: 14, lineHeight: 21 },
  stepRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  stepNum: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  stepNumText: { color: '#FFF', fontSize: 13, fontWeight: '700' },
  stepContent: { flex: 1 },
  stepTitle: { fontSize: 14, fontWeight: '600', marginBottom: 3 },
  stepDetail: { fontSize: 13, lineHeight: 19 },
  criteriaRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  checkBox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  criteriaText: { flex: 1, fontSize: 13, lineHeight: 19 },
  strikethrough: { textDecorationLine: 'line-through', opacity: 0.6 },
  resourceRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, borderBottomWidth: 1 },
  resourceTitle: { flex: 1, fontSize: 13, fontWeight: '500' },
  resourceTypeBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  resourceTypeText: { fontSize: 11, fontWeight: '600' },
  doneBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 10, paddingHorizontal: 18, borderRadius: 20, borderWidth: 1 },
  doneBtnText: { fontSize: 15, fontWeight: '700' },
});

